import { Link } from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Messages } from "../../../../../core/api";
import { auth } from "../../../../../core/auth";
import useApi from "../../../../../shared/hooks/useApi";
import type { CaregiverId, PatientId } from "../../../../../shared/schema/schema";
import { currencyFormatter } from "../../../../../shared/utils/currency-formatter";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import EntityProfileActionBar from "../../../../entityProfileModal/components/EntityProfileActionBar";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import type { CaregiverProfileBridgeActions } from "../../../caregiverProfileModal.actions";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiver?: CaregiverProfileCaregiver;
  caregiverId: CaregiverId;
};

type CaregiverPayRate = {
  id: number;
  createdAt?: unknown;
  payCodeId?: number | string | null;
  payCodeName?: string;
  payrollCodeName?: string;
  payrollCodeDisplayId?: string;
  type?: "FIXED_AMOUNT" | "FIXED_DIFF" | "PERCENTAGE" | string;
  hourly?: number | null;
  daily?: number | null;
  startDate?: unknown;
  endDate?: unknown;
  patientId?: PatientId | null;
  patientDisplayName?: string | null;
  patientDisplayId?: string | number | null;
};

type CaregiverPayRatesResponse = {
  caregiverPayRates?: CaregiverPayRate[];
};

type SortKey =
  | "createdAt"
  | "payCodeName"
  | "rateType"
  | "hourly"
  | "daily"
  | "startDate"
  | "patient";

export default function CaregiverProfilePayRatesSection(props: Props) {
  const { api } = useApi();
  const canEdit = auth.isAllowedTo(
    "edit_caregiver_page_pay_rates" as Messages["AgencyMemberPermission"]
  );
  const queryClient = useQueryClient();
  const queryKey = ["caregiverProfileModal", props.caregiverId, "payRates"];
  const payRatesQuery = useQuery({
    queryKey,
    queryFn: () =>
      api.anyRequest<CaregiverPayRatesResponse>(
        "GET",
        "agencies/:agencyId/agency_members/:agencyMemberId/caregivers/:caregiverId/pay_rates",
        { path: { caregiverId: props.caregiverId } }
      ),
  });

  const rows = useMemo(
    () => calculateCaregiverPayRateRanges(payRatesQuery.data?.caregiverPayRates ?? []),
    [payRatesQuery.data?.caregiverPayRates]
  );
  const invalidate = () => void queryClient.invalidateQueries({ queryKey });
  const openPayRateModal = (row?: CaregiverPayRate) => {
    if (props.caregiver === undefined) {
      return;
    }

    props.actions.openCaregiverPayRate({
      caregiver: props.caregiver,
      data:
        row === undefined
          ? undefined
          : {
              ...row,
              startDate: formatIsoLocalDate(row.startDate),
              endDate: formatIsoLocalDate(row.endDate),
            },
      onDone: invalidate,
    });
  };

  const columns: Array<EntityProfileDataTableColumn<CaregiverPayRate, SortKey>> = [
    {
      key: "createdAt",
      header: "Updated At",
      sortKey: "createdAt",
      sortValue: (row) => String(row.createdAt ?? ""),
      render: (row) => formatDate(row.createdAt),
    },
    {
      key: "payCodeName",
      header: "Payroll Code Name",
      sortKey: "payCodeName",
      sortValue: (row) => getPayCodeName(row),
      render: (row) => getPayCodeName(row),
    },
    {
      key: "rateType",
      header: "Rate Type",
      sortKey: "rateType",
      sortValue: (row) => formatRateType(row.type),
      render: (row) => formatRateType(row.type),
    },
    {
      key: "hourly",
      header: "Hourly Rate",
      sortKey: "hourly",
      sortValue: (row) => row.hourly,
      render: (row) => formatRateValue(row.type, row.hourly),
    },
    {
      key: "daily",
      header: "Daily Rate",
      sortKey: "daily",
      sortValue: (row) => row.daily,
      render: (row) => formatRateValue(row.type, row.daily),
    },
    {
      key: "dateRange",
      header: "Date Range",
      sortKey: "startDate",
      sortValue: (row) => String(row.startDate ?? ""),
      render: (row) => `${formatDate(row.startDate)} - ${formatDate(row.endDate)}`,
    },
    {
      key: "patient",
      header: "Patient",
      sortKey: "patient",
      sortValue: (row) => getPatientLabel(row),
      render: (row) => {
        const patientId = row.patientId;

        return patientId === null || patientId === undefined ? (
          ""
        ) : (
          <Link
            color="#4c91f7"
            textDecoration="underline"
            onClick={() => props.actions.navigateToPatient(patientId)}
          >
            {getPatientLabel(row)}
          </Link>
        );
      },
    },
    ...(canEdit
      ? [
          {
            key: "action",
            header: "",
            render: (row: CaregiverPayRate) => (
              <EntityModalButton
                aria-label="Edit pay rate"
                disabled={props.caregiver === undefined}
                iconClassName="fa fa-pencil"
                kind="orange"
                title="Edit pay rate"
                onClick={() => openPayRateModal(row)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load caregiver pay rates."
      isError={payRatesQuery.isError}
      isLoading={payRatesQuery.isLoading}
      loadingLabel="Loading caregiver pay rates"
      onRetry={() => void payRatesQuery.refetch()}
    >
      {canEdit ? (
        <EntityProfileActionBar mb={3}>
          <EntityModalButton
            disabled={props.caregiver === undefined}
            iconClassName="fa fa-undo"
            kind="red"
            size="sm"
            onClick={() =>
              props.caregiver === undefined
                ? undefined
                : props.actions.openCaregiverPayRateReset({
                    caregiver: props.caregiver,
                    onDone: invalidate,
                  })
            }
          >
            Reset Overrides
          </EntityModalButton>
          <EntityModalButton
            disabled={props.caregiver === undefined}
            iconClassName="fa fa-plus"
            kind="blue"
            size="sm"
            onClick={() => openPayRateModal()}
          >
            Add caregiver pay rate
          </EntityModalButton>
        </EntityProfileActionBar>
      ) : null}
      <EntityProfileDataTable
        columns={columns}
        defaultPageSize={10}
        defaultSort={{ key: "startDate", direction: "desc" }}
        getRowKey={(row, index) => row.id ?? index}
        pageSizeOptions={[10, 25, 50, 100]}
        rows={rows}
        showEmptyState={false}
      />
    </EntityProfileQuerySection>
  );
}

function getPayCodeName(row: CaregiverPayRate) {
  return row.payCodeName ?? row.payrollCodeName ?? row.payrollCodeDisplayId ?? "";
}

function calculateCaregiverPayRateRanges(rows: CaregiverPayRate[]) {
  const rowsByGroup = new Map<string, Array<CaregiverPayRate & { originalIndex: number }>>();

  rows.forEach((row, originalIndex) => {
    const startDate = toYmd(row.startDate);
    const endDate = toYmd(row.endDate);

    if (startDate === null || endDate === null) {
      return;
    }

    const groupKey = [row.payCodeId ?? getPayCodeName(row), row.patientId ?? ""].join(":");
    const group = rowsByGroup.get(groupKey) ?? [];
    group.push({
      ...row,
      startDate,
      endDate,
      originalIndex,
    });
    rowsByGroup.set(groupKey, group);
  });

  return Array.from(rowsByGroup.values()).flatMap(calculateRangesForGroup);
}

function calculateRangesForGroup(rows: Array<CaregiverPayRate & { originalIndex: number }>) {
  const boundaries = new Set<number>();

  rows.forEach((row) => {
    boundaries.add(toDayNumber(row.startDate));
    boundaries.add(toDayNumber(row.endDate) + 1);
  });

  const sortedBoundaries = Array.from(boundaries).sort((a, b) => a - b);
  const ranges: CaregiverPayRate[] = [];

  for (let index = 0; index < sortedBoundaries.length - 1; index += 1) {
    const startDay = sortedBoundaries[index];
    const endDay = sortedBoundaries[index + 1] - 1;
    const activeRows = rows.filter(
      (row) => toDayNumber(row.startDate) <= startDay && toDayNumber(row.endDate) >= endDay
    );

    if (activeRows.length === 0) {
      continue;
    }

    const newestRow = activeRows.sort(compareNewestPayRate)[activeRows.length - 1];
    const previousRange = ranges[ranges.length - 1];
    const startDate = fromDayNumber(startDay);
    const endDate = fromDayNumber(endDay);

    if (
      previousRange !== undefined &&
      previousRange.id === newestRow.id &&
      toDayNumber(previousRange.endDate) + 1 === startDay
    ) {
      previousRange.endDate = endDate;
      continue;
    }

    ranges.push({
      ...newestRow,
      startDate,
      endDate,
    });
  }

  return ranges;
}

function compareNewestPayRate(
  first: CaregiverPayRate & { originalIndex: number },
  second: CaregiverPayRate & { originalIndex: number }
) {
  const createdAtDiff = toCreatedAtMs(first.createdAt) - toCreatedAtMs(second.createdAt);

  return createdAtDiff === 0 ? first.originalIndex - second.originalIndex : createdAtDiff;
}

function toYmd(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const stringValue =
    typeof value === "string" || typeof value === "number"
      ? String(value)
      : typeof value === "object" && "toString" in value && typeof value.toString === "function"
      ? value.toString()
      : "";
  const ymd = stringValue.slice(0, 10);

  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : null;
}

function toDayNumber(value: unknown) {
  const ymd = toYmd(value);

  if (ymd === null) {
    return Number.NaN;
  }

  const [year, month, day] = ymd.split("-").map(Number);

  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

function fromDayNumber(dayNumber: number) {
  return new Date(dayNumber * 86_400_000).toISOString().slice(0, 10);
}

function toCreatedAtMs(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const parsed = Date.parse(String(value));

  return Number.isNaN(parsed) ? 0 : parsed;
}

function getPatientLabel(row: CaregiverPayRate) {
  const name =
    row.patientDisplayName ??
    (row.patientId === null || row.patientId === undefined ? "" : `Patient ${row.patientId}`);

  return row.patientDisplayId === null ||
    row.patientDisplayId === undefined ||
    row.patientDisplayId === ""
    ? name
    : `${name} (${row.patientDisplayId})`;
}

function formatRateType(type: CaregiverPayRate["type"]) {
  switch (type) {
    case "FIXED_AMOUNT":
      return "Fixed rate";
    case "FIXED_DIFF":
      return "Fixed rate raise (+)";
    case "PERCENTAGE":
      return "Percentage rate raise (%)";
    default:
      return String(type ?? "");
  }
}

function formatRateValue(type: CaregiverPayRate["type"], value: number | null | undefined) {
  if (value === null || value === undefined) {
    return "";
  }

  if (type === "PERCENTAGE") {
    return `${value}%`;
  }

  const formatted = currencyFormatter.formatCents(value);

  return type === "FIXED_DIFF" ? `+${formatted}` : formatted;
}

function formatDate(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return dateFormatter.toDate(value as never);
}

function formatIsoLocalDate(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return value;
  }

  if (typeof value === "string") {
    return value.slice(0, 10);
  }

  if (typeof value === "object" && "toJSON" in value && typeof value.toJSON === "function") {
    return String(value.toJSON()).slice(0, 10);
  }

  if (typeof value === "object" && "toString" in value && typeof value.toString === "function") {
    return String(value.toString()).slice(0, 10);
  }

  return value;
}
